import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Chat as ChatIcon,
  ContentPaste,
  FolderOpen,
  Fullscreen,
  FullscreenExit,
  PlayArrow,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import {
  REMOTE_SUPPORT_ACTION_LIST,
  RemoteSupportActionDef,
} from '../../constants/remoteSupportActions';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveScreenSubscription } from '../../hooks/useLiveScreenSubscription';
import { useRemoteControl } from '../../hooks/useRemoteControl';
import LiveActivityFeed from './LiveActivityFeed';
import ActivitySnapshotModal from './ActivitySnapshotModal';
import { formatRemoteControlLastStatus } from '../../utils/formatRemoteControlLastStatus';

export interface LiveScreenViewerProps {
  deviceId: string;
  computerName?: string;
  employeeName?: string | null;
  campaignName?: string | null;
  workState?: string | null;
  clockStatus?: string | null;
  liveScreenDeviceEnabled?: boolean;
  liveScreenGlobalEnabled?: boolean;
  liveScreenStatusLabel?: string | null;
  remoteControlDeviceEnabled?: boolean;
  remoteControlGlobalEnabled?: boolean;
}

function formatAgo(date: Date | null): string {
  if (!date) return '—';
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}

/** Map pointer position into object-fit:contain content ratios (0..1). */
function ratiosFromPointer(
  img: HTMLImageElement,
  clientX: number,
  clientY: number,
): { xRatio: number; yRatio: number } | null {
  const rect = img.getBoundingClientRect();
  const natW = img.naturalWidth || 0;
  const natH = img.naturalHeight || 0;
  if (rect.width <= 0 || rect.height <= 0 || natW <= 0 || natH <= 0) return null;

  const scale = Math.min(rect.width / natW, rect.height / natH);
  const contentW = natW * scale;
  const contentH = natH * scale;
  const left = rect.left + (rect.width - contentW) / 2;
  const top = rect.top + (rect.height - contentH) / 2;

  const x = (clientX - left) / contentW;
  const y = (clientY - top) / contentH;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return {
    xRatio: Math.min(1, Math.max(0, x)),
    yRatio: Math.min(1, Math.max(0, y)),
  };
}

function modeChipLabel(status: string, isControlling: boolean): string {
  if (isControlling) return 'Controlling';
  if (status === 'CONTROL_REQUESTING' || status === 'CONNECTING') return 'Requesting Control';
  if (status === 'CONTROL_SUSPENDED') return 'Control Suspended';
  if (status === 'CONTROL_ENDED') return 'Control Ended';
  if (status === 'ERROR') return 'Control Error';
  return 'View Only';
}

const LiveScreenViewer: React.FC<LiveScreenViewerProps> = ({
  deviceId,
  computerName,
  employeeName,
  campaignName,
  workState,
  clockStatus,
  liveScreenDeviceEnabled = true,
  liveScreenGlobalEnabled = true,
  liveScreenStatusLabel,
  remoteControlDeviceEnabled = false,
  remoteControlGlobalEnabled = false,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canConfigure = permissions.includes('monitoring.live_screen.configure');
  const canViewLiveActivity = permissions.includes('monitoring.recorded_activity.read');
  const canViewSnapshots = permissions.includes('monitoring.action_snapshots.view');
  const canStartControl = permissions.includes('monitoring.remote_control.start');
  const canReadRemoteControl = permissions.includes('monitoring.remote_control.read')
    || canStartControl;
  const canMouse = permissions.includes('monitoring.remote_control.mouse');
  const canKeyboard = permissions.includes('monitoring.remote_control.keyboard');
  const canFullscreen = permissions.includes('monitoring.remote_control.fullscreen');
  const canClipboardSend = permissions.includes('monitoring.remote_control.clipboard.send');
  const canClipboardReceive = permissions.includes('monitoring.remote_control.clipboard.receive');
  const canChat = permissions.includes('monitoring.remote_control.chat');
  const canFilesUpload = permissions.includes('monitoring.remote_control.files.upload');
  const canFilesDownload = permissions.includes('monitoring.remote_control.files.download');
  const canActionsExecute = permissions.includes('monitoring.remote_control.actions.execute');
  const queryClient = useQueryClient();
  const correlationId = useId();
  const startedAtRef = useRef(Date.now());
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [reason, setReason] = useState('');
  const [rcReason, setRcReason] = useState('');
  const [activityPanelCollapsed, setActivityPanelCollapsed] = useState(!isDesktop);
  const [snapshotModal, setSnapshotModal] = useState<{ id: string; capturedAt?: string | null } | null>(null);
  const [clipboardOpen, setClipboardOpen] = useState(false);
  const [clipboardText, setClipboardText] = useState('');
  const [clipboardBusy, setClipboardBusy] = useState(false);
  const [clipboardError, setClipboardError] = useState<string | null>(null);
  const [clipboardInfo, setClipboardInfo] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatDraft, setChatDraft] = useState('');
  const [chatSendError, setChatSendError] = useState<string | null>(null);
  const [filesOpen, setFilesOpen] = useState(false);
  const [filesStatus, setFilesStatus] = useState<string | null>(null);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [filesBusy, setFilesBusy] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [diagnosticsLogLines, setDiagnosticsLogLines] = useState<string[] | null>(null);

  const {
    status: controlStatus,
    isControlling,
    errorMessage: controlError,
    startControl,
    stopControl,
    sendMouseMove,
    sendMouseDown,
    sendMouseUp,
    sendMouseWheel,
    sendKeyDown,
    sendKeyUp,
    sendClipboardToRemote,
    getClipboardFromRemote,
    sendChatMessage,
    chatMessages,
    unreadChatCount,
    openChat,
    clearUnread,
    closeChat,
    chatSessionEnded,
    requestRemoteFilePick,
    uploadFileToRemote,
    cancelFileTransfer,
    executeSupportAction,
    fileTransfers,
    fileReady,
    actionResults,
    clearFileReady,
  } = useRemoteControl();

  const { data: actionsQuery } = useQuery({
    queryKey: ['remoteSupportActions'],
    queryFn: async () => {
      try {
        const res = await apiClient.monitoring.listRemoteSupportActions();
        return res ?? { data: { success: true, data: REMOTE_SUPPORT_ACTION_LIST } };
      } catch {
        return { data: { success: true, data: REMOTE_SUPPORT_ACTION_LIST } };
      }
    },
    enabled: canActionsExecute && isControlling && actionsOpen,
    staleTime: 60_000,
    retry: false,
  });

  const supportActions: RemoteSupportActionDef[] = (() => {
    const raw = actionsQuery?.data?.data;
    const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.actions) ? raw.actions : null);
    if (list && list.length > 0) {
      return list
        .map((a: any) => ({
          id: String(a.id || a.actionId || ''),
          label: String(a.label || a.id || a.actionId || ''),
          description: String(a.description || ''),
        }))
        .filter((a: RemoteSupportActionDef) => Boolean(a.id));
    }
    return REMOTE_SUPPORT_ACTION_LIST;
  })();

  useEffect(() => {
    setActivityPanelCollapsed(!isDesktop);
  }, [isDesktop]);

  // Release control on unmount only (do not re-stop when callback identity changes)
  const stopControlRef = useRef(stopControl);
  stopControlRef.current = stopControl;
  useEffect(() => () => {
    void stopControlRef.current();
  }, []);

  const configBlocks = !liveScreenGlobalEnabled || !liveScreenDeviceEnabled;

  const { frameUrl, state, lastFrameAt, connecting, error } = useLiveScreenSubscription({
    deviceId,
    mode: 'DETAIL',
    enabled: Boolean(deviceId) && !configBlocks,
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.monitoring.updateDeviceLiveScreen(deviceId, {
        enabled,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      setReason('');
      queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
      queryClient.invalidateQueries({ queryKey: ['monitoringLiveWall'] });
    },
  });

  const toggleRcMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.monitoring.updateDeviceRemoteControl(deviceId, {
        enabled,
        reason: rcReason.trim() || undefined,
      }),
    onSuccess: () => {
      setRcReason('');
      queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
      queryClient.invalidateQueries({ queryKey: ['remoteControlStatus', deviceId] });
      queryClient.invalidateQueries({ queryKey: ['remoteControlDiagnostics', deviceId] });
    },
  });

  const { data: rcDiagData } = useQuery({
    queryKey: ['remoteControlDiagnostics', deviceId],
    queryFn: () => apiClient.monitoring.getRemoteControlDiagnostics(deviceId),
    enabled: Boolean(deviceId) && canReadRemoteControl,
    refetchInterval: 30000,
    retry: false,
  });
  const rcDiag = rcDiagData?.data?.data;

  useEffect(() => {
    if (!deviceId || configBlocks) return undefined;
    startedAtRef.current = Date.now();
    void Promise.resolve(
      apiClient.monitoring.auditLiveScreenView(deviceId, {
        event: 'start',
        mode: 'DETAIL',
        correlation_id: correlationId,
      }),
    ).catch(() => undefined);

    return () => {
      const durationSeconds = Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000));
      void Promise.resolve(
        apiClient.monitoring.auditLiveScreenView(deviceId, {
          event: 'end',
          mode: 'DETAIL',
          correlation_id: correlationId,
          duration_seconds: durationSeconds,
        }),
      ).catch(() => undefined);
    };
  }, [deviceId, correlationId, configBlocks]);

  // Focus viewport for keyboard input while controlling
  useEffect(() => {
    if (isControlling) {
      viewportRef.current?.focus?.();
    }
  }, [isControlling]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Escape exits fullscreen only — does not stop control
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && document.fullscreenElement) {
        void document.exitFullscreen().catch(() => undefined);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  const toggleFullscreen = async () => {
    const el = viewportRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      // ignore
    }
  };

  const mapEvent = useCallback((e: React.PointerEvent | React.MouseEvent | React.WheelEvent) => {
    const img = imageRef.current;
    if (!img) return null;
    return ratiosFromPointer(img, e.clientX, e.clientY);
  }, []);

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isControlling || !canMouse) return;
    const ratios = mapEvent(e);
    if (!ratios) return;
    sendMouseMove(ratios.xRatio, ratios.yRatio);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!isControlling || !canMouse) return;
    const ratios = mapEvent(e);
    if (!ratios) return;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    sendMouseDown(ratios.xRatio, ratios.yRatio, e.button);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!isControlling || !canMouse) return;
    const ratios = mapEvent(e);
    if (!ratios) return;
    sendMouseUp(ratios.xRatio, ratios.yRatio, e.button);
  };

  const onWheel = (e: React.WheelEvent) => {
    if (!isControlling || !canMouse) return;
    e.preventDefault();
    const ratios = mapEvent(e);
    if (!ratios) return;
    sendMouseWheel(ratios.xRatio, ratios.yRatio, e.deltaY);
  };

  const onContextMenu = (e: React.MouseEvent) => {
    if (isControlling) e.preventDefault();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!isControlling || !canKeyboard) return;
    if (e.key === 'Escape' && document.fullscreenElement) return;
    e.preventDefault();
    e.stopPropagation();
    const code = e.keyCode || (e.nativeEvent as KeyboardEvent).which || 0;
    if (!code) return;
    sendKeyDown(code, code);
  };

  const onKeyUp = (e: React.KeyboardEvent) => {
    if (!isControlling || !canKeyboard) return;
    if (e.key === 'Escape' && document.fullscreenElement) return;
    e.preventDefault();
    e.stopPropagation();
    const code = e.keyCode || (e.nativeEvent as KeyboardEvent).which || 0;
    if (!code) return;
    sendKeyUp(code, code);
  };

  const showClipboardButton = isControlling && (canClipboardSend || canClipboardReceive);
  const showChatButton = isControlling && canChat;
  const showFilesButton = isControlling && (canFilesUpload || canFilesDownload);
  const showActionsButton = isControlling && canActionsExecute;

  const handleOpenClipboard = () => {
    setClipboardError(null);
    setClipboardInfo(null);
    setClipboardOpen(true);
  };

  const handleOpenChat = () => {
    setChatSendError(null);
    setChatOpen(true);
    openChat();
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    closeChat();
    clearUnread();
  };

  const handleOpenFiles = () => {
    setFilesError(null);
    setFilesStatus(null);
    setFilesOpen(true);
  };

  const handleOpenActions = () => {
    setActionError(null);
    setActionStatus(null);
    setActionsOpen(true);
  };

  useEffect(() => {
    if (!chatOpen || !chatListRef.current) return;
    chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
  }, [chatMessages, chatOpen]);

  useEffect(() => {
    if (!fileReady || !filesOpen) return;
    setFilesStatus(`File ready: ${fileReady.fileName}`);
    setFilesError(null);
  }, [fileReady, filesOpen]);

  useEffect(() => {
    if (!actionsOpen || actionResults.length === 0) return;
    const latest = actionResults[actionResults.length - 1];
    if (latest.ok) {
      setActionStatus(latest.resultSummary || `${latest.actionId} completed`);
      setActionError(null);
    } else {
      setActionError(latest.error || `${latest.actionId} failed`);
    }
    if (
      latest.actionId === 'COLLECT_DIAGNOSTICS'
      && Array.isArray(latest.recentLogLines)
      && latest.recentLogLines.length > 0
    ) {
      setDiagnosticsLogLines(latest.recentLogLines);
    }
    setActionBusyId(null);
  }, [actionResults, actionsOpen]);

  const handleSendClipboard = async () => {
    if (!canClipboardSend || clipboardBusy) return;
    setClipboardBusy(true);
    setClipboardError(null);
    setClipboardInfo(null);
    try {
      const result = await sendClipboardToRemote(clipboardText);
      if (!result.ok) {
        setClipboardError(result.error || result.reason || 'Failed to send clipboard');
      } else {
        setClipboardInfo(
          result.charCount != null
            ? `Sent ${result.charCount} character(s) to remote clipboard.`
            : 'Sent to remote clipboard.',
        );
      }
    } catch (err: any) {
      setClipboardError(err?.message || 'Failed to send clipboard');
    } finally {
      setClipboardBusy(false);
    }
  };

  const handleGetClipboard = async () => {
    if (!canClipboardReceive || clipboardBusy) return;
    setClipboardBusy(true);
    setClipboardError(null);
    setClipboardInfo(null);
    try {
      const result = await getClipboardFromRemote();
      if (!result.ok) {
        setClipboardError(result.error || result.reason || 'Failed to get remote clipboard');
      } else {
        setClipboardText(result.text ?? '');
        setClipboardInfo(
          result.charCount != null
            ? `Received ${result.charCount} character(s) from remote.`
            : 'Received remote clipboard.',
        );
      }
    } catch (err: any) {
      setClipboardError(err?.message || 'Failed to get remote clipboard');
    } finally {
      setClipboardBusy(false);
    }
  };

  const handleSendChat = async () => {
    if (!canChat || !chatDraft.trim()) return;
    setChatSendError(null);
    try {
      await sendChatMessage(chatDraft);
      setChatDraft('');
    } catch (err: any) {
      setChatSendError(err?.message || 'Failed to send message');
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canFilesUpload) return;
    setFilesBusy(true);
    setFilesError(null);
    setFilesStatus(`Uploading ${file.name}…`);
    const result = await uploadFileToRemote(file);
    setFilesBusy(false);
    if (!result.ok) {
      setFilesError(result.error || 'Upload failed');
      setFilesStatus(null);
    } else {
      setFilesStatus(`Uploaded ${file.name}`);
    }
  };

  const handleRequestRemoteFile = async () => {
    if (!canFilesDownload || filesBusy) return;
    setFilesBusy(true);
    setFilesError(null);
    setFilesStatus('Waiting for employee to pick a file…');
    clearFileReady();
    const result = await requestRemoteFilePick();
    setFilesBusy(false);
    if (!result.ok) {
      setFilesError(result.error || 'Request failed');
      setFilesStatus(null);
    }
  };

  const handleDownloadReadyFile = async () => {
    if (!fileReady?.transferId || !canFilesDownload) return;
    setFilesBusy(true);
    setFilesError(null);
    try {
      await apiClient.monitoring.downloadRemoteControlTransferContent(
        fileReady.transferId,
        fileReady.fileName,
      );
      setFilesStatus(`Downloaded ${fileReady.fileName}`);
    } catch (err: any) {
      setFilesError(err?.response?.data?.message || err?.message || 'Download failed');
    } finally {
      setFilesBusy(false);
    }
  };

  const handleCancelTransfer = async (transferId: string) => {
    setFilesError(null);
    const result = await cancelFileTransfer(transferId);
    if (!result.ok) {
      setFilesError(result.error || 'Cancel failed');
    } else {
      setFilesStatus('Transfer cancelled');
    }
  };

  const handleExecuteAction = async (actionId: string) => {
    if (!canActionsExecute || actionBusyId) return;
    setActionBusyId(actionId);
    setActionError(null);
    setActionStatus(`Running ${actionId}…`);
    const result = await executeSupportAction(actionId);
    if (!result.ok) {
      setActionError(result.error || 'Action failed');
      setActionStatus(null);
      setActionBusyId(null);
    }
    // success toast comes from support_action_result listener
  };

  const stateReason = String(state?.reason || state?.capturePolicyReason || '');
  const disabledByState = stateReason === 'GLOBAL_DISABLED'
    || stateReason === 'DEVICE_DISABLED'
    || stateReason === 'DISABLED';

  let statusLabel: string;
  if (!liveScreenGlobalEnabled || stateReason === 'GLOBAL_DISABLED') {
    statusLabel = 'DISABLED GLOBALLY';
  } else if (!liveScreenDeviceEnabled || stateReason === 'DEVICE_DISABLED') {
    statusLabel = 'DISABLED FOR DEVICE';
  } else if (connecting) {
    statusLabel = 'CONNECTING';
  } else if (error) {
    statusLabel = 'ERROR';
  } else if (disabledByState || state?.streamAvailable === false || state?.captureAllowed === false) {
    statusLabel = String(state?.reasonText || state?.reason || liveScreenStatusLabel || 'UNAVAILABLE');
  } else if (frameUrl) {
    statusLabel = 'LIVE';
  } else {
    statusLabel = String(state?.reasonText || 'WAITING FOR FRAME');
  }

  const statusColor: 'success' | 'warning' | 'error' | 'default' | 'info' =
    statusLabel === 'LIVE'
      ? 'success'
      : statusLabel.includes('CONNECTING') || statusLabel.includes('WAITING')
        ? 'info'
        : statusLabel.includes('ERROR')
          ? 'error'
          : 'warning';

  const showFrame = Boolean(frameUrl) && !configBlocks && !disabledByState;
  const rcAvailable = remoteControlGlobalEnabled && remoteControlDeviceEnabled;
  const showStartControl = canStartControl
    && (canMouse || canKeyboard)
    && !isControlling
    && controlStatus !== 'CONTROL_REQUESTING'
    && controlStatus !== 'CONNECTING';

  const liveScreenPanel = (
    <Box minWidth={0}>
      <Paper
        ref={viewportRef}
        variant="outlined"
        tabIndex={isControlling ? 0 : -1}
        onKeyDown={isControlling ? onKeyDown : undefined}
        onKeyUp={isControlling ? onKeyUp : undefined}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          bgcolor: '#111',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: isControlling ? '2px solid' : 'none',
          outlineColor: 'error.main',
          cursor: isControlling && canMouse ? 'crosshair' : 'default',
        }}
      >
        {isControlling && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'error.main',
              color: 'error.contrastText',
              typography: 'caption',
              fontWeight: 800,
              letterSpacing: 0.6,
              pointerEvents: 'none',
            }}
          >
            REMOTE CONTROL ACTIVE
          </Box>
        )}
        {showFrame ? (
          <Box
            component="img"
            ref={imageRef}
            src={frameUrl!}
            alt="Live screen"
            draggable={false}
            onPointerMove={isControlling ? onPointerMove : undefined}
            onPointerDown={isControlling ? onPointerDown : undefined}
            onPointerUp={isControlling ? onPointerUp : undefined}
            onWheel={isControlling ? onWheel : undefined}
            onContextMenu={onContextMenu}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              userSelect: 'none',
              pointerEvents: isControlling ? 'auto' : 'none',
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: 'grey.400', px: 2, textAlign: 'center' }}>
            {error || statusLabel}
          </Typography>
        )}
      </Paper>
    </Box>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" mb={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Live Screen{computerName ? ` · ${computerName}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[employeeName || 'Not currently identified', campaignName || 'No campaign', workState || clockStatus || '—']
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            View Only until Start Control. Monitor: Primary. Live Screen enable permits on-demand capture — not continuous streaming.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Chip size="small" color={statusColor} label={String(statusLabel).toUpperCase()} />
          <Chip
            size="small"
            color={isControlling ? 'error' : 'default'}
            variant={isControlling ? 'filled' : 'outlined'}
            label={modeChipLabel(controlStatus, isControlling)}
            data-testid="remote-control-mode-chip"
          />
          <Typography variant="caption" color="text.secondary">
            Last frame · {formatAgo(lastFrameAt)}
          </Typography>
          {showStartControl && (
            <Button
              size="small"
              variant="contained"
              color="warning"
              disabled={!rcAvailable || !showFrame}
              onClick={() => void startControl(deviceId, 0)}
              data-testid="start-control-button"
            >
              Start Control
            </Button>
          )}
          {showClipboardButton && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentPaste fontSize="small" />}
              onClick={handleOpenClipboard}
              data-testid="clipboard-button"
            >
              Clipboard
            </Button>
          )}
          {showFilesButton && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FolderOpen fontSize="small" />}
              onClick={handleOpenFiles}
              data-testid="files-button"
            >
              Files
            </Button>
          )}
          {showChatButton && (
            <Badge
              color="error"
              badgeContent={unreadChatCount}
              invisible={unreadChatCount <= 0}
              data-testid="chat-unread-badge"
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<ChatIcon fontSize="small" />}
                onClick={handleOpenChat}
                data-testid="chat-button"
              >
                Chat
              </Button>
            </Badge>
          )}
          {showActionsButton && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PlayArrow fontSize="small" />}
              onClick={handleOpenActions}
              data-testid="actions-button"
            >
              Actions
            </Button>
          )}
          {canFullscreen && (
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <IconButton size="small" onClick={toggleFullscreen} aria-label="Toggle fullscreen">
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          )}
          {isControlling && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => void stopControl()}
              data-testid="stop-control-button"
            >
              Stop Control
            </Button>
          )}
        </Box>
      </Box>

      {controlError && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {controlError}
        </Alert>
      )}
      {canStartControl && !rcAvailable && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {!remoteControlGlobalEnabled
            ? 'Remote Control is disabled in Workforce Monitoring configuration.'
            : 'Remote Control is disabled for this computer.'}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>Live Screen Access</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Status:{' '}
          {!liveScreenGlobalEnabled
            ? 'Disabled globally'
            : liveScreenDeviceEnabled
              ? 'Enabled for this computer'
              : 'Disabled for this computer'}
        </Typography>
        {canConfigure && liveScreenGlobalEnabled && (
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {!liveScreenDeviceEnabled && (
              <TextField
                size="small"
                label="Optional reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ minWidth: 240 }}
              />
            )}
            <Button
              size="small"
              variant="contained"
              disabled={toggleMutation.isPending}
              onClick={() => toggleMutation.mutate(!liveScreenDeviceEnabled)}
            >
              {liveScreenDeviceEnabled ? 'Disable Live Screen' : 'Enable Live Screen'}
            </Button>
          </Box>
        )}
        {!liveScreenGlobalEnabled && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Live Screen is disabled by Workforce Monitoring configuration.
          </Alert>
        )}
        {liveScreenGlobalEnabled && !liveScreenDeviceEnabled && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Live Screen has been disabled for this computer.
          </Alert>
        )}
      </Paper>

      {canStartControl && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }} data-testid="remote-control-access-panel">
          <Typography variant="subtitle2" gutterBottom>Remote Control Access</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Status:{' '}
            {!remoteControlGlobalEnabled
              ? 'Disabled globally'
              : remoteControlDeviceEnabled
                ? 'Enabled for this computer'
                : 'Disabled for this computer'}
          </Typography>
          {rcDiag && (
            <Box
              display="flex"
              gap={0.75}
              flexWrap="wrap"
              mb={1}
              data-testid="remote-control-diagnostics"
            >
              <Chip
                size="small"
                variant="outlined"
                color={rcDiag.agentConnected ? 'success' : 'default'}
                label={rcDiag.agentConnected ? 'Agent connected' : 'Agent offline'}
              />
              {rcDiag.secureDesktop ? (
                <Chip size="small" color="warning" label="Secure desktop" />
              ) : null}
              {rcDiag.windowsLocked ? (
                <Chip size="small" color="warning" label="Windows locked" />
              ) : null}
              {formatRemoteControlLastStatus(rcDiag.lastStatusSummary ?? rcDiag.lastStatus) ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Last status · ${formatRemoteControlLastStatus(rcDiag.lastStatusSummary ?? rcDiag.lastStatus)!.slice(0, 48)}`}
                />
              ) : null}
              {rcDiag.policyReasonText && !rcDiag.policyAllowed ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={String(rcDiag.policyReasonText).slice(0, 64)}
                />
              ) : null}
            </Box>
          )}
          {remoteControlGlobalEnabled && (
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              {!remoteControlDeviceEnabled && (
                <TextField
                  size="small"
                  label="Optional reason"
                  value={rcReason}
                  onChange={(e) => setRcReason(e.target.value)}
                  sx={{ minWidth: 240 }}
                />
              )}
              <Button
                size="small"
                variant="contained"
                disabled={toggleRcMutation.isPending}
                onClick={() => toggleRcMutation.mutate(!remoteControlDeviceEnabled)}
              >
                {remoteControlDeviceEnabled ? 'Disable Remote Control' : 'Enable Remote Control'}
              </Button>
            </Box>
          )}
        </Paper>
      )}
      {!canStartControl && canReadRemoteControl && rcDiag && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }} data-testid="remote-control-diagnostics-readonly">
          <Typography variant="subtitle2" gutterBottom>Remote Control Diagnostics</Typography>
          <Box display="flex" gap={0.75} flexWrap="wrap" data-testid="remote-control-diagnostics">
            <Chip
              size="small"
              variant="outlined"
              color={rcDiag.agentConnected ? 'success' : 'default'}
              label={rcDiag.agentConnected ? 'Agent connected' : 'Agent offline'}
            />
            {rcDiag.secureDesktop ? (
              <Chip size="small" color="warning" label="Secure desktop" />
            ) : null}
            {rcDiag.windowsLocked ? (
              <Chip size="small" color="warning" label="Windows locked" />
            ) : null}
            {formatRemoteControlLastStatus(rcDiag.lastStatusSummary ?? rcDiag.lastStatus) ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Last status · ${formatRemoteControlLastStatus(rcDiag.lastStatusSummary ?? rcDiag.lastStatus)!.slice(0, 48)}`}
              />
            ) : null}
          </Box>
        </Paper>
      )}

      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        alignItems="stretch"
      >
        <Box flex={{ md: '1 1 70%' }} minWidth={0}>
          {liveScreenPanel}
        </Box>
        {canViewLiveActivity && (
          <Box
            flex={{ md: activityPanelCollapsed ? '0 0 auto' : '0 0 30%' }}
            width={{ xs: '100%', md: activityPanelCollapsed ? 'auto' : '30%' }}
            minWidth={{ md: activityPanelCollapsed ? 56 : 280 }}
            maxWidth={{ md: activityPanelCollapsed ? 56 : '30%' }}
          >
            <LiveActivityFeed
              deviceId={deviceId}
              enabled={Boolean(deviceId)}
              collapsed={activityPanelCollapsed}
              onToggleCollapse={() => setActivityPanelCollapsed((prev) => !prev)}
              canViewSnapshots={canViewSnapshots}
              onViewSnapshot={(snapshotId, capturedAt) => setSnapshotModal({ id: snapshotId, capturedAt })}
            />
          </Box>
        )}
      </Box>

      <ActivitySnapshotModal
        open={Boolean(snapshotModal)}
        onClose={() => setSnapshotModal(null)}
        snapshotId={snapshotModal?.id || null}
        capturedAt={snapshotModal?.capturedAt}
      />

      <Dialog
        open={clipboardOpen}
        onClose={() => setClipboardOpen(false)}
        fullWidth
        maxWidth="sm"
        data-testid="clipboard-dialog"
      >
        <DialogTitle>Remote Clipboard</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Explicit transfer only — clipboard is never auto-synced.
          </Typography>
          <TextField
            label="Clipboard text"
            value={clipboardText}
            onChange={(e) => setClipboardText(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            inputProps={{ 'data-testid': 'clipboard-text-input' }}
          />
          {clipboardError && (
            <Alert severity="error" sx={{ mt: 1.5 }} data-testid="clipboard-error">
              {clipboardError}
            </Alert>
          )}
          {clipboardInfo && (
            <Alert severity="success" sx={{ mt: 1.5 }} data-testid="clipboard-info">
              {clipboardInfo}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {canClipboardSend && (
            <Button
              variant="contained"
              disabled={clipboardBusy || !clipboardText.trim()}
              onClick={() => void handleSendClipboard()}
              data-testid="send-clipboard-button"
            >
              Send Clipboard to Remote
            </Button>
          )}
          {canClipboardReceive && (
            <Button
              variant="outlined"
              disabled={clipboardBusy}
              onClick={() => void handleGetClipboard()}
              data-testid="get-clipboard-button"
            >
              Get Clipboard from Remote
            </Button>
          )}
          <Box flex={1} />
          <Button onClick={() => setClipboardOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={handleCloseChat}
        PaperProps={{ sx: { width: { xs: '100%', sm: 380 } } }}
        data-testid="chat-drawer"
      >
        <Box display="flex" flexDirection="column" height="100%" p={2} gap={1.5}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Support Chat</Typography>
            <Button size="small" onClick={handleCloseChat}>Close</Button>
          </Box>
          {(chatSessionEnded || controlStatus === 'CONTROL_ENDED' || (!isControlling && chatMessages.length > 0)) && (
            <Alert severity="info" data-testid="chat-session-ended">
              Remote session ended
            </Alert>
          )}
          <Box
            ref={chatListRef}
            flex={1}
            overflow="auto"
            border={1}
            borderColor="divider"
            borderRadius={1}
            p={1.5}
            data-testid="chat-message-list"
          >
            {chatMessages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No messages yet.</Typography>
            ) : (
              chatMessages.map((msg) => (
                <Box key={msg.id} mb={1.25} data-testid={`chat-message-${msg.from.toLowerCase()}`}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {msg.from === 'OPERATOR' ? 'You' : msg.from === 'EMPLOYEE' ? 'Employee' : 'System'}
                    {' · '}
                    {new Date(msg.at).toLocaleTimeString()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontStyle: msg.from === 'SYSTEM' ? 'italic' : 'normal',
                      color: msg.from === 'SYSTEM' ? 'text.secondary' : 'text.primary',
                    }}
                  >
                    {msg.text}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
          {chatSendError && (
            <Alert severity="error" data-testid="chat-send-error">{chatSendError}</Alert>
          )}
          <Box display="flex" gap={1}>
            <TextField
              size="small"
              fullWidth
              placeholder={canChat && isControlling ? 'Type a message…' : 'Chat unavailable'}
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              disabled={!canChat || !isControlling || chatSessionEnded}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSendChat();
                }
              }}
              inputProps={{ 'data-testid': 'chat-input' }}
            />
            <Button
              variant="contained"
              disabled={!canChat || !isControlling || chatSessionEnded || !chatDraft.trim()}
              onClick={() => void handleSendChat()}
              data-testid="chat-send-button"
            >
              Send
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={filesOpen}
        onClose={() => setFilesOpen(false)}
        fullWidth
        maxWidth="sm"
        data-testid="files-dialog"
      >
        <DialogTitle>Remote Files</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Transfers go to the employee TEKS Support folder. Operators cannot enter arbitrary remote paths.
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            data-testid="files-upload-input"
            onChange={(e) => void handleFileInputChange(e)}
          />
          <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
            {canFilesUpload && (
              <Button
                variant="contained"
                disabled={filesBusy || !isControlling}
                onClick={() => fileInputRef.current?.click()}
                data-testid="files-upload-button"
              >
                Upload File to Remote
              </Button>
            )}
            {canFilesDownload && (
              <Button
                variant="outlined"
                disabled={filesBusy || !isControlling}
                onClick={() => void handleRequestRemoteFile()}
                data-testid="files-download-request-button"
              >
                Request File from Remote
              </Button>
            )}
            {canFilesDownload && fileReady?.transferId && (
              <Button
                variant="contained"
                color="success"
                disabled={filesBusy}
                onClick={() => void handleDownloadReadyFile()}
                data-testid="files-download-ready-button"
              >
                Download {fileReady.fileName}
              </Button>
            )}
          </Box>
          {fileTransfers.filter((t) => t.status === 'uploading' || t.status === 'awaiting_remote').map((t) => (
            <Box key={t.transferId} mb={1.5} data-testid={`file-transfer-${t.transferId}`}>
              <Typography variant="caption" display="block" gutterBottom>
                {t.fileName} · {t.status}
                {t.status === 'uploading' ? ` · ${t.progress}%` : ''}
              </Typography>
              {t.status === 'uploading' && (
                <LinearProgress variant="determinate" value={t.progress} sx={{ mb: 0.75 }} />
              )}
              {t.status === 'awaiting_remote' && <LinearProgress sx={{ mb: 0.75 }} />}
              <Button
                size="small"
                color="warning"
                onClick={() => void handleCancelTransfer(t.transferId)}
                data-testid={`file-transfer-cancel-${t.transferId}`}
              >
                Cancel
              </Button>
            </Box>
          ))}
          {filesError && (
            <Alert severity="error" sx={{ mb: 1 }} data-testid="files-error">{filesError}</Alert>
          )}
          {filesStatus && (
            <Alert severity="info" sx={{ mb: 1 }} data-testid="files-status">{filesStatus}</Alert>
          )}
          {fileTransfers.length > 0 && (
            <List dense data-testid="files-transfer-list">
              {fileTransfers.slice().reverse().map((t) => (
                <ListItem key={`${t.transferId}-${t.status}`} disableGutters>
                  <ListItemText
                    primary={`${t.direction}: ${t.fileName}`}
                    secondary={`${t.status}${t.error ? ` — ${t.error}` : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        fullWidth
        maxWidth={diagnosticsLogLines?.length ? 'md' : 'sm'}
        data-testid="actions-dialog"
      >
        <DialogTitle>Support Actions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Only allowlisted actions can be run. Free-text commands are not available.
          </Typography>
          <Box display="flex" flexDirection="column" gap={1} data-testid="actions-list">
            {supportActions.map((action) => (
              <Box key={action.id} display="flex" alignItems="flex-start" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!isControlling || Boolean(actionBusyId)}
                  onClick={() => void handleExecuteAction(action.id)}
                  data-testid={`action-button-${action.id}`}
                >
                  {actionBusyId === action.id ? <CircularProgress size={16} /> : action.label}
                </Button>
                {action.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ pt: 0.75 }}>
                    {action.description}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          {actionError && (
            <Alert severity="error" sx={{ mt: 1.5 }} data-testid="actions-error">{actionError}</Alert>
          )}
          {actionStatus && (
            <Alert severity="success" sx={{ mt: 1.5 }} data-testid="actions-status">{actionStatus}</Alert>
          )}
          {diagnosticsLogLines && diagnosticsLogLines.length > 0 && (
            <Box sx={{ mt: 2 }} data-testid="diagnostics-recent-logs">
              <Typography variant="subtitle2" gutterBottom>
                Recent agent.log lines (redacted)
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  maxHeight: 280,
                  overflow: 'auto',
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    m: 0,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {diagnosticsLogLines.join('\n')}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveScreenViewer;
